import { z } from 'zod';
/**
 * Four-tier severity for watchlist-driven signals. `critical` is the only
 * level that should ever pull an analyst off another task; everything
 * else is "read during your morning brief". Matches the palette hook in
 * signals-stream.tsx (critical = `--color-critical`, high = accent-bright,
 * medium = accent, low = ink-muted).
 */
export const SignalSeverity = z.enum(['critical', 'high', 'medium', 'low']);
/**
 * What kind of change triggered a Signal. Used for filter chips in the UI
 * and for measuring which detection paths are actually useful in telemetry.
 *
 *   new-fact            — a new :Fact edge attached to the entity
 *   new-source          — a source doc mentioning the entity appeared
 *   relationship-change — the entity's k=1 neighborhood shifted
 *   anomaly             — a non-structural heuristic (shell pattern, gap
 *                         detection, etc.)
 */
export const SignalKind = z.enum([
    'new-fact',
    'new-source',
    'relationship-change',
    'anomaly',
]);
/**
 * One piece of evidence backing a signal. Every signal card must carry at
 * least one of these (product spec §6 — "every fact rendered with
 * provenance"); otherwise the card is a vibe, not a defensible claim.
 */
export const SignalEvidence = z.object({
    text: z.string(),
    sourceDocId: z.string().optional(),
    confidence: z.number().min(0).max(1),
});
/**
 * Per-user surfaced signal, written by the nightly cron and read by the
 * command-center feed. Lives at `signals/{id}` in Firestore.
 *
 * Ownership: `ownerUid` scopes visibility (rules only grant read when
 * auth uid matches). The only client-writable field is `dismissedAt` —
 * everything else comes from the server.
 */
export const Signal = z.object({
    id: z.string(),
    orgId: z.string(),
    ownerUid: z.string(),
    entityName: z.string(),
    title: z.string(),
    summary: z.string(),
    severity: SignalSeverity,
    kind: SignalKind,
    tags: z.array(z.string()).default([]),
    evidence: z.array(SignalEvidence).default([]),
    createdAt: z.string(),
    dismissedAt: z.string().nullable().optional(),
});
